<script lang="ts">
	import { Button, ButtonType, ButtonSize, Icon } from '@repo/commons/components'

	interface Props {
		title?: string
		totalPages: number
		currentPage: number
		scale: number
		isFullScreen: boolean
		disabled: boolean
		onPageChange: (newPage: number, scrollBehavior: 'smooth' | 'instant') => void
		onZoomIn: () => void
		onZoomOut: () => void
		onClose?: () => void
		onDownload?: () => void
		onToggleFullScreen?: () => void
	}

	const {
		title,
		totalPages,
		currentPage,
		scale,
		disabled,
		isFullScreen,
		onPageChange,
		onZoomIn,
		onZoomOut,
		onDownload,
		onToggleFullScreen,
		onClose
	}: Props = $props()
</script>

<div class="header">
	<div class="title">
		<Icon name="pdf" size="sm" class="text-[#002C4D80]" />
		<span>
			{title}
		</span>
	</div>

	<div class="controls">
		{#if totalPages}
			<div class="pagination">
				<Button
					buttonType={ButtonType.TERTIARY}
					buttonSize={ButtonSize.SMALL}
					onclick={() => onPageChange(currentPage - 1, 'smooth')}
					disabled={currentPage === 1 || disabled}
					rounded
					iconProps={{ name: 'arrowLeftBold', size: 'md', class: 'text-[#002C4D80]' }}
				/>
				<div class="page-indicator">
					<span> Page </span>
					<input
						type="number"
						class="page-indicator-input"
						value={currentPage}
						max={totalPages}
						min="1"
						disabled={totalPages <= 1 || disabled}
						onchange={(e) => onPageChange(e.target?.value, 'instant')}
					/>
					<span>
						/ {totalPages}
					</span>
				</div>
				<Button
					buttonType={ButtonType.TERTIARY}
					buttonSize={ButtonSize.SMALL}
					onclick={() => onPageChange(currentPage + 1, 'smooth')}
					disabled={currentPage === totalPages || disabled}
					rounded
					iconProps={{ name: 'arrowRightBold', size: 'md', class: 'text-[#002C4D80]' }}
				/>
			</div>
		{/if}

		<div class="zoom-controls">
			<Button
				buttonText=""
				buttonType={ButtonType.TERTIARY}
				buttonSize={ButtonSize.SMALL}
				onclick={onZoomOut}
				disabled={scale <= 0.5 || disabled}
				rounded
				iconProps={{ name: 'minusThin', size: 'md', class: 'text-[#002C4D80]' }}
			/>
			<div class="zoom-indicator">{Math.round(scale * 100)}%</div>
			<Button
				buttonText=""
				buttonType={ButtonType.TERTIARY}
				buttonSize={ButtonSize.SMALL}
				onclick={onZoomIn}
				disabled={scale >= 3.0 || disabled}
				rounded
				iconProps={{ name: 'plus', size: 'md', class: 'text-[#002C4D80]' }}
			/>
		</div>
	</div>

	<div class="actions">
		<div class="action-buttons">
			<Button
				buttonText=""
				buttonType={ButtonType.TERTIARY}
				buttonSize={ButtonSize.SMALL}
				{disabled}
				rounded
				iconProps={{ name: 'downloadFilled', size: 'md', class: 'text-[#002C4D80]' }}
				onclick={onDownload}
			/>
			<Button
				buttonText=""
				buttonType={ButtonType.TERTIARY}
				buttonSize={ButtonSize.SMALL}
				{disabled}
				rounded
				iconProps={{
					name: isFullScreen ? 'collapse' : 'fullScreen',
					size: 'md',
					class: 'text-[#002C4D80]'
				}}
				onclick={onToggleFullScreen}
			/>
			<Button
				buttonText=""
				buttonType={ButtonType.TERTIARY}
				buttonSize={ButtonSize.SMALL}
				rounded
				iconProps={{ name: 'close', size: 'md', class: 'text-[#002C4D80]' }}
				onclick={onClose}
			/>
		</div>
	</div>
</div>

<style>
	.header {
		@apply sticky top-0 flex items-center justify-center p-4 bg-white border-b flex-wrap gap-2 w-full;
	}

	.title {
		@apply flex items-center gap-2 text-base text-[#001626E5] overflow-hidden text-ellipsis whitespace-nowrap;
		flex: 1 1 0%;
		min-width: 0;
	}

	.title span {
		@apply overflow-hidden text-ellipsis whitespace-nowrap;
		min-width: 0;
	}

	.controls {
		@apply flex items-center gap-8 justify-center;
		flex: 0 0 auto;
	}

	.actions {
		@apply flex items-center gap-1;
		flex: 1 1 0%;
		justify-content: flex-end;
	}

	.action-buttons {
		@apply flex items-center gap-1;
	}

	.zoom-controls {
		@apply flex items-center gap-2;
	}
	.zoom-indicator {
		@apply text-base text-[#001626E5] w-10 text-center;
	}
	.actions {
		@apply flex items-center gap-1;
	}
	.pagination {
		@apply flex justify-center items-center gap-4;
	}
	.page-indicator {
		@apply text-base text-[#001626E5] flex items-center gap-2;
	}
	.page-indicator-input {
		@apply text-base text-[#001626E5] bg-[#00498011] w-10 text-center rounded-md p-0.5;
		&:focus-within {
			@apply outline outline-2 outline-brand outline-offset-2;
		}
	}
</style>
